import { jwtDecode } from 'jwt-decode';

// Duality for the SPA: dev defaults to the local backend/hub; the production build
// (vite build reads .env.production) sets these to same-origin '/' so the app calls
// the backend through the SAME Front Door hostname (no CORS, no hardcoded host).
// The corporate port sets different VITE_* values; the code never hardcodes a side.
export const LOGIN_URL = import.meta.env.VITE_LOGIN_URL ?? 'http://localhost:3000/';
export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/';

// Cloud build: log in with Entra (same-origin) instead of the on-prem Login-hub.
// Chosen at build time via VITE_AUTH_MODE (set only in the cloud .env.production) —
// no user-facing checkbox. On-prem/dev leaves it unset and keeps the hub flow.
export const CLOUD_AUTH = import.meta.env.VITE_AUTH_MODE === 'entra';

// export const BASE_URL = 'http://galaxy.corp.altopenergy.com:8000/';
// export const LOGIN_URL = 'http://galaxy.corp.altopenergy.com:3000/';

export const FILE_PICKER_URL = `${BASE_URL}filepicker/getNextChildren`;

// --- Access-token store (in-memory) ----------------------------------------------------------------
// The access token lives ONLY in memory, so it is cleared on page reload / browser restart — that is
// what makes a restart log the user out (the refresh token is a session cookie that also dies on
// restart). On every cold load the token is re-established by a single silent refresh against the
// HttpOnly cookie ("bootstrap"), driven by isAuthenticated()/apiFetch below. All app code reads the
// token through getAccessToken(), so this storage change is transparent to callers. The REFRESH token
// is never stored in JS at all — it lives only in the HttpOnly `altop_refresh` cookie set by the backend.
let accessToken = null;

export const getAccessToken = () => accessToken;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const removeTokens = () => {
  accessToken = null;
};

export const isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp < Date.now() / 1000;
  } catch (e) {
    return true;
  }
};

// True when the token expires within `bufferSeconds` (default 5 min) — used to refresh proactively
// before a request rather than waiting for a 401.
const isTokenExpiringSoon = (token, bufferSeconds = 300) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp - Date.now() / 1000 < bufferSeconds;
  } catch (e) {
    return true;
  }
};

// --- Silent refresh (single-flight) ----------------------------------------------------------------
// Replaces the old full-page redirect to the Login hub (that round-trip was the "white page" flash).
// Refresh is now a background fetch against the HttpOnly refresh cookie; concurrent callers (multiple
// API calls, the proactive timer, several tabs) share ONE in-flight request via `refreshPromise`.
let refreshPromise = null;
let refreshTimerId = null;

export const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}login/refresh-token/`, {
        method: 'POST',
        credentials: 'include', // send the HttpOnly altop_refresh cookie
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: '{}',
      });
      if (!response.ok) return null;
      const data = await response.json();
      const token = data && data.access_token;
      if (!token) return null;
      setAccessToken(token);
      scheduleTokenRefresh();
      try { if (authChannel) authChannel.postMessage({ type: 'refreshed' }); } catch (e) { /* ignore */ }
      return token;
    } catch (e) {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Proactively refresh ~60s before the current token's exp so an active session never hits an expiry.
export const scheduleTokenRefresh = () => {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  const token = getAccessToken();
  if (!token) return;
  let delayMs;
  try {
    const decoded = jwtDecode(token);
    delayMs = (decoded.exp - 60) * 1000 - Date.now();
  } catch (e) {
    return;
  }
  if (delayMs < 0) delayMs = 0;
  refreshTimerId = setTimeout(() => { refreshAccessToken(); }, delayMs);
};

// --- Cross-tab sync --------------------------------------------------------------------------------
// All tabs of one browser share the cookie; broadcast logout so the others don't keep a dead session.
let authChannel = null;
try {
  authChannel = new BroadcastChannel('altop_auth');
  authChannel.onmessage = (ev) => {
    if (ev && ev.data && ev.data.type === 'logout') {
      removeTokens();
      redirectToLogin();
    }
  };
} catch (e) {
  authChannel = null;
}

export const hasPermission = (requiredPermission) => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    if (decoded.permissions.includes(requiredPermission) || !requiredPermission) {
      return true;
    }
    else {
      return false;
    }
  } catch (e) {
    return false;
  }
};

export const getUserEmail = () => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    return decoded.email;
  } catch (e) {
    return false;
  }
};

export const getUserName = () => {
  const token = getAccessToken();
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    return decoded.username;
  } catch (e) {
    return false;
  }
};

// --- App-specific export preserved from the pre-migration auth.js ----------------------------------
// `getFullName` is imported by OffboardingTasks.jsx / OnboardingTasks.jsx and is NOT part of the
// canonical auth module. It is self-contained so it is appended here to keep the app building.
// Flagged as an ANOMALY for review.
export const getFullName = () => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    const email = decoded.email ?? '';
    const local = email.split('@')[0] || '';
    const [first = '', last = ''] = local.split('.');
    const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
    return `${cap(first)} ${cap(last)}`.trim();
  } catch (e) {
    return null;
  }
};

export const extractTokenFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
};

// Retained for the cross-site fallback app (the Azure-hosted overview), which still hands off ?token=.
// A no-op for the same-site apps that now rely on the cookie.
export const setTokenFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const iso = params.get('iso');
  const mode = params.get('mode');
  if (token) {
    setAccessToken(token);
    const newURL = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, newURL);
  }
  return { token, iso, mode };
};

export const redirectToLogin = () => {
  removeTokens();
  if (CLOUD_AUTH) {
    // Same-origin Entra start (BASE_URL is '/' in the cloud build). The backend
    // callback sets the refresh cookie and the app silent-refreshes to log in.
    window.location.href = `${BASE_URL}login/entra/start/?redirect=${encodeURIComponent(window.location.href)}`;
    return;
  }
  window.location.href = `${LOGIN_URL}login?redirect=${encodeURIComponent(window.location.href)}`;
};

export const logout = async () => {
  try {
    await fetch(`${BASE_URL}login/logout/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: '{}',
    });
  } catch (e) {
    /* best effort — clear the client regardless */
  }
  removeTokens();
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  try { if (authChannel) authChannel.postMessage({ type: 'logout' }); } catch (e) { /* ignore */ }
  // Cloud: route through the backend Entra end-session endpoint. Bouncing to the app
  // root alone does NOT log the user out — the Microsoft SSO session stays live and the
  // next redirectToLogin silently re-authenticates. /login/entra/logout/ clears the
  // cookie AND terminates the IdP session. (The on-prem hub URL isn't reachable in cloud.)
  window.location.href = CLOUD_AUTH ? `${BASE_URL}login/entra/logout/` : `${LOGIN_URL}login`;
};

// Valid token -> true. Missing/expired -> ONE silent refresh -> true if it yields a token, else false.
// No full-page redirect here (that was the white-page cause); the caller decides what to do on false.
export const isAuthenticated = async () => {
  const token = getAccessToken();
  if (token && !isTokenExpired(token)) {
    scheduleTokenRefresh();
    return true;
  }
  const refreshed = await refreshAccessToken();
  return !!refreshed;
};

export const apiFetch = async (url, options = {}) => {
  // Tolerate a leading slash in the path: with same-origin BASE_URL='/', a caller
  // passing '/login/apps' would otherwise yield '//login/apps', which the browser
  // treats as protocol-relative (host "login" -> ERR_NAME_NOT_RESOLVED). Strip it
  // so both '/login/apps' and 'login/apps/' resolve the same (safe in dev too).
  const path = url.startsWith('/') ? url.slice(1) : url;
  const fetchWithToken = (token) => fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  try {
    let token = getAccessToken();

    // Bootstrap: on a cold load / hard refresh / app-switch the in-memory token is null. Refresh ONCE
    // up front instead of sending an empty Bearer and eating a guaranteed 401. refreshAccessToken() is
    // single-flight, so concurrent first calls collapse to one network refresh.
    if (!token) {
      token = await refreshAccessToken();
    }

    // Proactive: refresh before sending if the token is about to expire, avoiding a sure 401 round-trip.
    if (token && isTokenExpiringSoon(token)) {
      const refreshed = await refreshAccessToken();
      if (refreshed) token = refreshed;
    }

    let response = await fetchWithToken(token);

    // Reactive: on 401, do one silent refresh and retry once (was: return null with no retry).
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        response = await fetchWithToken(newToken);
      } else {
        redirectToLogin();
        return response;
      }
    }

    if (!response.ok) {
      let error = {};
      try { error = await response.json(); } catch (e) { /* non-JSON error body */ }
      // Prefer the backend's `error`, then `message` — the latter carries the duality
      // "available on the on-premises HR app only" text for file-bound features gated
      // off in the cloud (HTTP 501, {onpremOnly:true}), so components surface that
      // instead of a generic failure.
      throw new Error(error.error || error.message || 'Network response was not ok');
    }

    return response;
  } catch (error) {
    console.error('API Fetch Error:', error);
    throw error;
  }
};

// --- App-specific export preserved from the pre-migration auth.js ----------------------------------
// `redirectToApp` is imported by MiniDashboardDrawer.jsx and is NOT part of the canonical auth module.
// It is self-contained (uses only functions already defined above) so it is appended here to keep the
// app building. Flagged as an ANOMALY for review.
export const redirectToApp = async (appUrl, requiredPermission, onUnauthorized) => {
  const token = getAccessToken();
  if (!token || isTokenExpired(token)) {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      logout();
      return;
    }
  }

  try {
    const user = jwtDecode(getAccessToken());
    if (user && user.permissions && (user.permissions.includes(requiredPermission) || !requiredPermission)) {
      const url = new URL(appUrl);
      // Cloud apps self-authenticate via Entra SSO (the same-site cookie), so never
      // hand off ?token= in the cloud build; on-prem keeps the token hand-off except
      // for the same-site apps already listed.
      if (!CLOUD_AUTH && !['http://hrtool.corp.altopenergy.com/', 'http://setool.corp.altopenergy.com/', 'http://baldaytool.corp.altopenergy.com/', 'https://altopmarketsoverview.z21.web.core.windows.net/'].includes(appUrl)) {
        url.searchParams.append('token', getAccessToken());
      }
      try {
        const response = await apiFetch('login/apps/');
        if (response) {
          const data = await response.json();
          url.searchParams.append("mode", data.defaultTheme || 'light');
        }
      } catch (error) {
        url.searchParams.append("mode", "light");
      }
      window.open(url.toString(), '_blank');
    } else {
      onUnauthorized();
    }
  } catch (error) {
    console.error('Error decoding token:', error);
    logout();
  }
};
